import React from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ApplyIcon from "@mui/icons-material/SaveAltRounded";
import { getReadableShortDate } from "../../../utils/commons";
import { resolveWodImage } from "../../../services/AssetConfigService";
import { TransformationWOD } from "./types";

interface ITransformationWODView {
  wod: TransformationWOD;
  assetBaseUrl?: string;
  assetLoading?: boolean;
  onDeleteWod: () => void;
  onCloneClicked: () => void;
}

export const TransformationWODView = ({
  wod,
  assetBaseUrl,
  assetLoading,
  onDeleteWod,
  onCloneClicked,
}: ITransformationWODView) => {
  // Absolute keys render immediately; relative keys once the base URL loads.
  const imageSrc = resolveWodImage(wod, assetBaseUrl);

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: 2,
        minHeight: 0,
        overflow: "hidden",
        pl: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Transformation Workout
          </Typography>
          <Chip
            label={getReadableShortDate(wod.date)}
            size="small"
            color="secondary"
            sx={{ height: 22, fontSize: "0.75rem", mt: 0.75 }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Use for today">
            <IconButton onClick={onCloneClicked} size="small" color="primary">
              <ApplyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete workout">
            <IconButton
              onClick={onDeleteWod}
              size="small"
              sx={{
                color: "error.main",
                "&:hover": { bgcolor: "rgba(244,67,54,0.08)" },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1,
        }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt="Transformation workout"
            style={{
              width: "100%",
              maxHeight: "100%",
              height: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              minHeight: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {assetLoading
                ? "Loading image..."
                : "No image found for this workout"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
